import { type ReactNode } from "react";

export type StepDirection = 1 | -1;

/**
 * What the input lets through as it is typed: digits, one leading minus, one point. `e`, `E` and `+`
 * are refused — `Number()` would read them as notation, which nobody typing a count means.
 *
 * Each digit run has exactly one quantifier that can match it. The shorter `/^-?\d*\.?\d*$/` accepts the
 * same strings, but with no point both `\d*` compete for the same digits — Sonar S5852 (backtracking).
 */
const NUMBER_DRAFT = /^-?(?:\d+(?:\.\d*)?|\.\d*)?$/;

export const isNumberDraft = (text: string) => NUMBER_DRAFT.test(text);

/** `null` for an empty field, `undefined` for a draft that is not a number yet (`-`, `.`). */
export const parseNumber = (text: string): number | null | undefined => {
  if (text === "") {
    return null;
  }
  const parsed = Number(text);

  return Number.isNaN(parsed) ? undefined : parsed;
};

export const formatNumber = (value: number | null | undefined) => (value == null ? "" : String(value));

export const clamp = (value: number, min?: number, max?: number) =>
  Math.min(Math.max(value, min ?? -Infinity), max ?? Infinity);

const decimals = (value: number) => String(value).split(".")[1]?.length ?? 0;

/**
 * One press of the stepper. Rounded to the finer of the two operands, so `0.1 + 0.2` lands on `0.3`
 * rather than `0.30000000000000004`. An empty field starts from zero, pulled into range.
 */
export const stepValue = (
  value: number | null,
  step: number,
  direction: StepDirection,
  min?: number,
  max?: number,
) => {
  if (value === null) {
    return clamp(0, min, max);
  }
  const next = Number((value + direction * step).toFixed(Math.max(decimals(value), decimals(step))));

  return clamp(next, min, max);
};

export const shouldShowSuffix = (suffix: ReactNode, text: string) => !!suffix && text !== "";

/** The on-screen keyboard: digits only for a whole step, with a point for a fractional one. */
export const inputModeFor = (step: number) => (Number.isInteger(step) ? "numeric" : "decimal");
